import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "@/components/ui/toast";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export function CreateTranslateDialog({
  open,
  onOpenChange,
  caseId,
  versionId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  versionId: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedLocales, setSelectedLocales] = useState<string[]>([]);

  const statusQuery = useQuery({
    queryKey: ["translate-status", versionId],
    queryFn: () => api.getTranslateStatus(versionId),
    enabled: open && Boolean(versionId),
  });

  const targetLocales = statusQuery.data?.targetLocales ?? [];
  const localeStatusByCode = useMemo(() => {
    const map = new Map(
      (statusQuery.data?.locales ?? []).map((l) => [l.locale, l] as const),
    );
    return map;
  }, [statusQuery.data?.locales]);

  useEffect(() => {
    if (!open) return;
    if (targetLocales.length === 0) return;
    setSelectedLocales(targetLocales.map((t) => t.locale));
  }, [open, targetLocales]);

  const startMutation = useMutation({
    mutationFn: (locales: string[]) =>
      api.startTranslate(versionId, { locales }),
    onSuccess: async (result) => {
      const okLocales = result.results
        .filter((r) => r.ok)
        .map((r) => r.targetLocale);
      toast.add({
        type: result.failed > 0 && result.started === 0 ? "error" : "success",
        title:
          result.started > 0
            ? `已启动 ${result.started} 个翻译`
            : "创建翻译失败",
        description:
          result.started > 0
            ? `已为 ${okLocales.join(", ")} 创建新版本。打开「版本」页查看进度。`
            : result.note ||
              (result.failed > 0
                ? `${result.failed} 个语言启动失败。`
                : "未启动任何流水线。"),
        priority: result.failed > 0 && result.started === 0 ? "high" : undefined,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["translate-status", versionId],
        }),
        queryClient.invalidateQueries({ queryKey: ["case", caseId] }),
        queryClient.invalidateQueries({ queryKey: ["pipeline-runs"] }),
      ]);
      if (result.started > 0) {
        onOpenChange(false);
        void navigate({
          to: "/cases/$caseId/versions",
          params: { caseId },
        });
      }
    },
    onError: (err) => {
      toast.add({
        type: "error",
        title: "创建翻译失败",
        description: err instanceof ApiError ? err.code : "translate_failed",
        priority: "high",
      });
    },
  });

  const placeholdersReady = statusQuery.data?.placeholdersReady ?? false;
  const busy = startMutation.isPending;
  const canSubmit =
    placeholdersReady && selectedLocales.length > 0 && !busy && !statusQuery.isError;

  const toggleLocale = (locale: string, checked: boolean) => {
    setSelectedLocales((prev) => {
      if (checked) {
        if (prev.includes(locale)) return prev;
        return [...prev, locale];
      }
      return prev.filter((l) => l !== locale);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" showCloseButton={!busy}>
        <DialogHeader>
          <DialogTitle>创建翻译</DialogTitle>
          <DialogDescription>
            每个选中语言将从此{" "}
            {statusQuery.data?.sourceVersion.locale ?? "en"}{" "}
            包派生为新版本。流水线在对应版本上运行；打开「版本」页查看。
          </DialogDescription>
        </DialogHeader>

        {statusQuery.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            加载语言…
          </div>
        ) : statusQuery.isError ? (
          <p className="text-sm text-destructive">
            无法加载此版本的翻译状态。
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {!placeholdersReady ? (
              <p className="text-sm text-destructive">
                源 phone JSON 尚未就绪。请先生成并保存源语言包再创建翻译。
              </p>
            ) : null}

            <Field>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <FieldLabel>语言</FieldLabel>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy || targetLocales.length === 0}
                    onClick={() =>
                      setSelectedLocales(targetLocales.map((t) => t.locale))
                    }
                  >
                    全选
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() => setSelectedLocales([])}
                  >
                    清空
                  </Button>
                </div>
              </div>
              <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-lg border border-border p-2">
                {targetLocales.length === 0 ? (
                  <p className="px-2 py-1.5 text-sm text-muted-foreground">
                    未配置目标语言。
                  </p>
                ) : (
                  targetLocales.map((entry) => {
                    const checked = selectedLocales.includes(entry.locale);
                    const existing = localeStatusByCode.get(entry.locale);
                    return (
                      <label
                        key={entry.locale}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm",
                          "hover:bg-muted/60",
                          checked ? "bg-muted/40" : undefined,
                        )}
                      >
                        <input
                          type="checkbox"
                          className="size-4 shrink-0 rounded border border-input accent-primary"
                          checked={checked}
                          disabled={busy}
                          onChange={(e) =>
                            toggleLocale(entry.locale, e.target.checked)
                          }
                        />
                        <span className="min-w-12 font-medium">
                          {entry.locale}
                        </span>
                        <span className="text-muted-foreground">
                          {entry.label}
                        </span>
                        {existing?.version ? (
                          <span className="ml-auto text-xs text-muted-foreground">
                            已有 v{existing.version.versionN}
                            {existing.run
                              ? ` · ${existing.run.status}`
                              : ""}
                          </span>
                        ) : (
                          <span className="ml-auto text-xs text-muted-foreground">
                            新建
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              <FieldDescription>
                {selectedLocales.length === 0
                  ? "请至少选择一种语言。"
                  : `将创建：${selectedLocales.join(", ")}。`}
              </FieldDescription>
            </Field>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            取消
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => startMutation.mutate(selectedLocales)}
          >
            {busy ? (
              <>
                <Spinner />
                创建中…
              </>
            ) : (
              "创建"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
